import React, { useRef, useEffect, useCallback } from 'react';
import LinkListView from './link-list-view';
import { LinkListContainerProps, TopNavLink } from '../types';

const LinkListContainer = ({
  activeSubMenu,
  emitNavClick,
  isMenuActive = false,
  linkSection,
  onClickLink,
  setActiveSubMenu,
  onReturnToSubMenu,
}: LinkListContainerProps) => {
  const linkRefs = useRef<Record<string, HTMLAnchorElement | null>>({});
  const firstLinkRef = useRef<HTMLAnchorElement | null>(null);

  // Focus the first link when the panel opens
  useEffect(() => {
    if (activeSubMenu === linkSection.name && firstLinkRef.current) {
      firstLinkRef.current.focus();
    }
  }, [activeSubMenu, linkSection.name]);

  const onLinkClick = useCallback((e: React.MouseEvent<HTMLAnchorElement>, link: TopNavLink) => {
    emitNavClick({
      nav_item_hierarchy_3: link.name,
    });
    onClickLink();
  }, [emitNavClick, onClickLink]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent, currentLink: TopNavLink, currentIndex: number) => {
    const { links } = linkSection;

    switch (e.key) {
      case 'ArrowDown': {
        e.preventDefault();
        const nextIndex = (currentIndex + 1) % links.length;
        const nextLinkId = links[nextIndex].id || links[nextIndex].name;
        linkRefs.current[nextLinkId]?.focus();
        break;
      }
      case 'ArrowUp': {
        e.preventDefault();
        const prevIndex = currentIndex === 0 ? links.length - 1 : currentIndex - 1;
        const prevLinkId = links[prevIndex].id || links[prevIndex].name;
        linkRefs.current[prevLinkId]?.focus();
        break;
      }
      case 'ArrowLeft': {
        e.preventDefault();
        // Return to sub-menu
        if (onReturnToSubMenu) {
          onReturnToSubMenu();
        }
        break;
      }
      case 'Tab': {
        if (e.shiftKey && onReturnToSubMenu) {
          // Shift+Tab should return to sub-menu
          e.preventDefault();
          onReturnToSubMenu();
        }
        break;
      }
      case 'Escape': {
        e.preventDefault();
        setActiveSubMenu(undefined);
        break;
      }
      case 'Home': {
        e.preventDefault();
        const firstLinkId = links[0].id || links[0].name;
        linkRefs.current[firstLinkId]?.focus();
        break;
      }
      case 'End': {
        e.preventDefault();
        const lastLinkId = links[links.length - 1].id || links[links.length - 1].name;
        linkRefs.current[lastLinkId]?.focus();
        break;
      }
      default:
        break;
    }
  }, [linkSection, onReturnToSubMenu, setActiveSubMenu]);

  return (
    <LinkListView
      activeSubMenu={activeSubMenu}
      firstLinkRef={firstLinkRef}
      handleKeyDown={handleKeyDown}
      isMenuActive={isMenuActive}
      linkRefs={linkRefs}
      linkSection={linkSection}
      onClickLink={onLinkClick}
      setActiveSubMenu={setActiveSubMenu}
    />
  );
};

export default LinkListContainer;
